import React, { useState } from 'react';
import { PageContainer } from '../../components/shared';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import AccountCircleTwoToneIcon from '@mui/icons-material/AccountCircleTwoTone';
import DescriptionTwoToneIcon from '@mui/icons-material/DescriptionTwoTone';
import LibraryBooksTwoToneIcon from '@mui/icons-material/LibraryBooksTwoTone';
import LockTwoToneIcon from '@mui/icons-material/LockTwoTone';
import MailTwoToneIcon from '@mui/icons-material/MailTwoTone';
import PhoneAndroidTwoToneIcon from '@mui/icons-material/PhoneAndroidTwoTone';
import PinDropTwoToneIcon from '@mui/icons-material/PinDropTwoTone';
import EditIcon from '@mui/icons-material/Edit';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';

const ProfilePage: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);

  return (
    <PageContainer title="Profile">
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            borderBottom: '1px solid',
            borderColor: 'divider',
            '& .MuiTab-root': {
              minHeight: 48,
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '0.875rem',
              color: 'text.secondary',
              '&.Mui-selected': { color: 'primary.main', fontWeight: 600 },
            },
            '& .MuiTabs-indicator': { height: 2, borderRadius: 1 },
          }}
        >
          <Tab icon={<AccountCircleTwoToneIcon />} iconPosition="start" label="Profile" />
          <Tab icon={<DescriptionTwoToneIcon />} iconPosition="start" label="Personal Details" />
          <Tab icon={<LibraryBooksTwoToneIcon />} iconPosition="start" label="My Account" />
          <Tab icon={<LockTwoToneIcon />} iconPosition="start" label="Change Password" />
        </Tabs>

        {/* Tab 0: Profile */}
        {activeTab === 0 && (
          <Box sx={{ pt: 3 }}>
            <Grid container spacing={3}>
              {/* Left column — User card */}
              <Grid item xs={12} lg={4}>
                <Card variant="outlined">
                  <CardHeader
                    title={
                      <Grid container spacing={2} alignItems="center">
                        <Grid item>
                          <Avatar sx={{ width: 56, height: 56 }} src="/images/avatar.png">
                            <PersonOutlineIcon sx={{ fontSize: 28 }} />
                          </Avatar>
                        </Grid>
                        <Grid item xs>
                          <Typography variant="subtitle1" fontWeight={600}>Admin User</Typography>
                          <Typography variant="subtitle2" color="text.secondary">System Administrator</Typography>
                        </Grid>
                        <Grid item>
                          <Chip label="Admin" size="small" color="primary" />
                        </Grid>
                      </Grid>
                    }
                  />
                  <Divider />
                  <CardContent sx={{ p: 0 }}>
                    <List disablePadding>
                      <ListItemButton>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <MailTwoToneIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText primary={<Typography variant="subtitle1">Email</Typography>} />
                        <Typography variant="subtitle2" color="text.secondary" align="right">admin@trueone.io</Typography>
                      </ListItemButton>
                      <Divider />
                      <ListItemButton>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <PhoneAndroidTwoToneIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText primary={<Typography variant="subtitle1">Phone</Typography>} />
                        <Typography variant="subtitle2" color="text.secondary" align="right">+61 400 000 000</Typography>
                      </ListItemButton>
                      <Divider />
                      <ListItemButton>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <PinDropTwoToneIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText primary={<Typography variant="subtitle1">Location</Typography>} />
                        <Typography variant="subtitle2" color="text.secondary" align="right">Melbourne</Typography>
                      </ListItemButton>
                    </List>
                    <CardContent>
                      <Grid container>
                        <Grid item xs={4}>
                          <Typography variant="h3" align="center">12</Typography>
                          <Typography variant="subtitle2" align="center" color="text.secondary">Inspections</Typography>
                        </Grid>
                        <Grid item xs={4}>
                          <Typography variant="h3" align="center">5</Typography>
                          <Typography variant="subtitle2" align="center" color="text.secondary">Templates</Typography>
                        </Grid>
                        <Grid item xs={4}>
                          <Typography variant="h3" align="center">3</Typography>
                          <Typography variant="subtitle2" align="center" color="text.secondary">Machines</Typography>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </CardContent>
                </Card>
              </Grid>

              {/* Right column — About & Details */}
              <Grid item xs={12} lg={8}>
                <Grid container spacing={3}>
                  {/* About me */}
                  <Grid item xs={12}>
                    <Card variant="outlined">
                      <CardHeader
                        title={<Typography variant="h5">About me</Typography>}
                        action={<IconButton size="small"><EditIcon fontSize="small" /></IconButton>}
                      />
                      <Divider />
                      <CardContent>
                        <Grid container spacing={2}>
                          <Grid item xs={12}>
                            <Typography variant="body2" color="text.secondary">
                              System administrator managing machine inspections, templates, and scheduling across the TrueOne platform. Responsible for operational safety and maintenance compliance.
                            </Typography>
                          </Grid>
                          <Grid item xs={12}>
                            <Typography variant="subtitle1" fontWeight={600}>Personal Details</Typography>
                          </Grid>
                          <Grid item xs={12}>
                            <TableContainer>
                              <Table size="small">
                                <TableBody>
                                  {[
                                    ['Full Name', 'Admin User'],
                                    ['Role', 'System Administrator'],
                                    ['Address', '123 Collins St, Melbourne VIC 3000'],
                                    ['Zip Code', '3000'],
                                    ['Phone', '+61 400 000 000'],
                                    ['Email', 'admin@trueone.io'],
                                  ].map(([label, value]) => (
                                    <TableRow key={label}>
                                      <TableCell component="th" sx={{ fontWeight: 500, border: 0, pl: 0, width: 140, color: 'text.primary' }}>{label}</TableCell>
                                      <TableCell sx={{ border: 0, width: 10, color: 'text.secondary' }}>:</TableCell>
                                      <TableCell sx={{ border: 0, color: 'text.secondary' }}>{value}</TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Work Information */}
                  <Grid item xs={12}>
                    <Card variant="outlined">
                      <CardHeader title={<Typography variant="h5">Work Information</Typography>} />
                      <Divider />
                      <CardContent>
                        <Grid container spacing={1}>
                          <Grid item xs={12}>
                            <Grid container>
                              <Grid item xs={12} sm={4}>
                                <Typography variant="subtitle1" fontWeight={600}>Current</Typography>
                                <Typography variant="subtitle2" color="text.secondary">Full-time</Typography>
                              </Grid>
                              <Grid item xs={12} sm={8}>
                                <Typography variant="subtitle1" fontWeight={600}>System Administrator</Typography>
                                <Typography variant="subtitle2" color="text.secondary">
                                  Managing inspections, scheduling, and team coordination across all sites.
                                </Typography>
                              </Grid>
                            </Grid>
                          </Grid>
                          <Grid item xs={12}><Divider /></Grid>
                          <Grid item xs={12}>
                            <Grid container>
                              <Grid item xs={12} sm={4}>
                                <Typography variant="subtitle1" fontWeight={600}>Department</Typography>
                                <Typography variant="subtitle2" color="text.secondary">Operations</Typography>
                              </Grid>
                              <Grid item xs={12} sm={8}>
                                <Typography variant="subtitle1" fontWeight={600}>Site: Melbourne Main</Typography>
                                <Typography variant="subtitle2" color="text.secondary">
                                  Primary facility with 15+ machines under active inspection schedules.
                                </Typography>
                              </Grid>
                            </Grid>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Tab 1: Personal Details (edit form) */}
        {activeTab === 1 && (
          <Box sx={{ pt: 3 }}>
            <Card variant="outlined">
              <CardHeader title={<Typography variant="h5">Personal Details</Typography>} />
              <Divider />
              <CardContent>
                <Grid container spacing={2.5}>
                  <Grid item xs={12} sm={6}>
                    <TextField label="First Name" defaultValue="Admin" fullWidth size="small" />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField label="Last Name" defaultValue="User" fullWidth size="small" />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField label="Email" defaultValue="admin@trueone.io" fullWidth size="small" />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField label="Phone" defaultValue="+61 400 000 000" fullWidth size="small" />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField label="Address" defaultValue="123 Collins St" fullWidth size="small" />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField label="City" defaultValue="Melbourne" fullWidth size="small" />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField label="State" defaultValue="VIC" fullWidth size="small" />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField label="Post Code" defaultValue="3000" fullWidth size="small" />
                  </Grid>
                </Grid>
                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                  <Button variant="contained" size="small">Save</Button>
                </Box>
              </CardContent>
            </Card>
          </Box>
        )}

        {/* Tab 2: My Account */}
        {activeTab === 2 && (
          <Box sx={{ pt: 3 }}>
            <Card variant="outlined">
              <CardHeader title={<Typography variant="h5">Account Settings</Typography>} />
              <Divider />
              <CardContent>
                <Grid container spacing={2.5}>
                  <Grid item xs={12} sm={6}>
                    <TextField label="Username" defaultValue="admin" fullWidth size="small" disabled />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField label="Role" defaultValue="Admin" fullWidth size="small" disabled />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField label="Status" defaultValue="Active" fullWidth size="small" disabled />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField label="Member Since" defaultValue="July 2026" fullWidth size="small" disabled />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Box>
        )}

        {/* Tab 3: Change Password */}
        {activeTab === 3 && (
          <Box sx={{ pt: 3 }}>
            <Card variant="outlined">
              <CardHeader title={<Typography variant="h5">Change Password</Typography>} />
              <Divider />
              <CardContent>
                <Grid container spacing={2.5}>
                  <Grid item xs={12} sm={6}>
                    <TextField label="Current Password" type="password" fullWidth size="small" />
                  </Grid>
                  <Grid item xs={12} sm={6} />
                  <Grid item xs={12} sm={6}>
                    <TextField label="New Password" type="password" fullWidth size="small" />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField label="Confirm Password" type="password" fullWidth size="small" />
                  </Grid>
                </Grid>
                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                  <Button variant="contained" size="small">Update Password</Button>
                </Box>
              </CardContent>
            </Card>
          </Box>
        )}
      </Grid>
    </Grid>
    </PageContainer>
  );
};

export default ProfilePage;
